import React from 'react';

const Languages = () => {
  const languages = [
    {
      name: "Arabic",
      level: 5,
      proficiency: "Native",
      flag: "🇹🇳",
      description: "Native speaker with excellent written and verbal communication skills"
    },
    {
      name: "English",
      level: 3,
      proficiency: "Intermediate",
      flag: "🇺🇸",
      description: "Good working proficiency in technical and academic contexts"
    },
    {
      name: "French",
      level: 2.5,
      proficiency: "Elementary",
      flag: "🇫🇷",
      description: "Basic conversational skills and reading comprehension"
    }
  ];

  const getStars = (level) => {
    const stars = [];
    const fullStars = Math.floor(level);
    const hasHalfStar = level % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={i} className="fas fa-star text-yellow-400"></i>);
    }
    
    if (hasHalfStar) {
      stars.push(<i key="half" className="fas fa-star-half-alt text-yellow-400"></i>);
    }
    
    const emptyStars = 5 - Math.ceil(level);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<i key={`empty-${i}`} className="far fa-star text-gray-300"></i>);
    }
    
    return stars;
  };

  const getProficiencyColor = (proficiency) => {
    switch (proficiency) {
      case 'Native': return 'from-green-500 to-green-600';
      case 'Intermediate': return 'from-blue-500 to-blue-600';
      case 'Elementary': return 'from-yellow-500 to-yellow-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  return (
    <section id="languages" className="section-padding bg-white/70 backdrop-blur-md shadow-lg">
      <div className="container-max">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Languages</h2>
          <div className="w-20 h-1 bg-primary-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Multilingual abilities that enable effective communication in diverse environments
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {languages.map((language, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl shadow-lg p-8 card-hover border border-gray-100 text-center"
            >
              <div className="mb-6">
                <div className="text-6xl mb-4">{language.flag}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{language.name}</h3>
                <div className={`inline-block px-4 py-2 bg-gradient-to-r ${getProficiencyColor(language.proficiency)} text-white rounded-full text-sm font-semibold mb-4`}>
                  {language.proficiency}
                </div>
              </div>

              <div className="mb-6">
                <div className="flex justify-center space-x-1 mb-3">
                  {getStars(language.level)}
                </div>
                <div className="text-gray-600 font-medium">
                  {language.level}/5
                </div>
              </div>

              <div className="mb-6">
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`bg-gradient-to-r ${getProficiencyColor(language.proficiency)} h-3 rounded-full transition-all duration-1000 ease-out`}
                    style={{ width: `${(language.level / 5) * 100}%` }}
                  ></div>
                </div>
              </div>

              <p className="text-gray-600 text-sm leading-relaxed">
                {language.description}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              <i className="fas fa-globe text-primary-600 mr-3"></i>
              Language Skills in Professional Context
            </h3>
            <div className="grid md:grid-cols-3 gap-8 mt-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-comments text-2xl text-green-600"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Communication</h4>
                <p className="text-gray-600 text-sm">
                  Effective verbal and written communication in Arabic and English for technical discussions
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-book text-2xl text-blue-600"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Documentation</h4>
                <p className="text-gray-600 text-sm">
                  Ability to read and write technical documentation in multiple languages
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-users text-2xl text-purple-600"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Collaboration</h4>
                <p className="text-gray-600 text-sm">
                  Experience working with international teams and diverse cultural backgrounds
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Languages;
